import { BASIC_AUTH, Configuration } from "../utils/configuration";
import { generateQueryParam } from "../utils/generateQueryParam";
import { APIOptions } from "./hooks/useApiBase";
import { useApiLazy, UseAPILazyProps } from "./hooks/useApiLazy";
import { BasicCommentInfo, Comments } from "./types/comments.types";

/**
 * Config
 */
const hostOverride = Configuration.gatewayUrl;

/**
 * Hooks
 */
// 🟦 GET /review/rating/v1/comments/{documentId} - Get all comments
export function useApiGetComments(
  options: APIOptions<undefined | Comments[]> = {}
) {
  const props: UseAPILazyProps<undefined | Comments[]> = {
    url: "",
    kind: "get",
    options,
    header: {
      Authorization: `Basic ${BASIC_AUTH}`,
    },
    hostOverride,
  };
  const api = useApiLazy<undefined | Comments[]>(props);
  const call = async (documentId: number) =>
    await api.call({
      ...props,
      url: `review/rating/v1/comments/${documentId}`,
    });
  return { ...api, call };
}

// 🟩 POST /review/rating/v1/comments - Create a new comment
export function useApiCreateComments(options: APIOptions<void> = {}) {
  const props: UseAPILazyProps<void> = {
    url: "review/rating/v1/comments",
    kind: "post",
    options,
    header: {
      Authorization: `Basic ${BASIC_AUTH}`,
    },
    hostOverride,
  };
  const api = useApiLazy<void>(props);
  const call = async (body: BasicCommentInfo) =>
    await api.call({
      ...props,
      body,
    });
  return { ...api, call };
}

// 🟩 POST /review/rating/v1/loggers/{documentId}- Create a new comment
export function useApiCreateLog(options: APIOptions<void> = {}) {
  const props: UseAPILazyProps<void> = {
    url: "",
    kind: "post",
    options,
    header: {
      Authorization: `Basic ${BASIC_AUTH}`,
    },
    hostOverride,
  };
  const api = useApiLazy<void>(props);
  const call = async (documentId: number) =>
    await api.call({
      ...props,
      url: generateQueryParam("review/rating/v1/loggers", { documentId }),
    });
  return { ...api, call };
}

// 🟥 DELETE /review/rating/v1/comments/{commentId} - Delete a comment
export function useApiDeleteComment(options: APIOptions<void> = {}) {
  const props: UseAPILazyProps<void> = {
    url: "",
    kind: "delete",
    options,
    header: {
      Authorization: `Basic ${BASIC_AUTH}`,
    },
    hostOverride,
  };
  const api = useApiLazy<void>(props);
  const call = async (commentId: string) =>
    await api.call({
      ...props,
      url: `review/rating/v1/comments/${commentId}`,
    });
  return { ...api, call };
}

//  🟧 PUT /review/rating/v1/comments/{commentId} - Update a comment's text
export function useApiUpdateComment(options: APIOptions<void> = {}) {
  const props: UseAPILazyProps<void> = {
    url: "",
    kind: "put",
    options,
    header: {
      Authorization: `Basic ${BASIC_AUTH}`,
    },
    hostOverride,
  };
  const api = useApiLazy<void>(props);
  const call = async (commentId: string, body: { updatedComment: string }) =>
    await api.call({
      ...props,
      url: `review/rating/v1/comments/${commentId}`,
      body,
    });
  return { ...api, call };
}
